/**
 * Formatos trocados entre a interface e a API do YDChat.
 */
import { z } from "zod";
import { usoSugestaoSchema } from "./copiloto";
import type { ResumoPonte } from "./sla";
import type {
  AutorMensagem,
  DirecaoMensagem,
  EtapaConversa,
  ModoConversa,
  Papel,
  StatusConversa,
  StatusMensagem,
  StatusPresenca,
  TipoMensagem,
} from "./dominio";
import type { StatusPedidoChat } from "./erp";

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email("E-mail inválido."),
  senha: z.string().min(1, "Informe a senha."),
});
export type LoginEntrada = z.infer<typeof loginSchema>;

export interface UsuarioSessao {
  id: string;
  nome: string;
  email: string;
  papel: Papel;
  statusPresenca: StatusPresenca;
  deveTrocarSenha: boolean;
  empresas: { id: string; nome: string }[];
}

export interface UsuarioResumo {
  id: string;
  nome: string;
  papel: Papel;
  statusPresenca: StatusPresenca;
  atendimentosAbertos: number;
}

export interface EtiquetaResumo {
  id: string;
  nome: string;
  cor: string;
  /** Etiqueta da cliente (vale para todas as conversas dela). */
  daCliente?: boolean;
}

/** Caixa de entrada: um número de WhatsApp da loja. */
export interface CaixaEntrada {
  id: string;
  empresaId: string;
  nome: string;
  telefone: string | null;
  principal: boolean;
  /** Só de comunicação (avisos automáticos): fora da espera e da fila. */
  comunicacao: boolean;
}

export interface ConversaResumo {
  id: string;
  empresaId: string;
  /** Caixa de entrada (número) por onde a conversa acontece. */
  canal: { id: string; nome: string; comunicacao: boolean };
  contato: {
    id: string;
    nome: string;
    telefone: string;
    erpClienteId: string | null;
  };
  responsavel: { id: string; nome: string } | null;
  status: StatusConversa;
  etapa: EtapaConversa;
  modo: ModoConversa;
  prioridade: number;
  etiquetas: EtiquetaResumo[];
  ultimaMensagem: { texto: string; direcao: DirecaoMensagem; em: string } | null;
  aguardandoRespostaDesde: string | null;
  naoLidas: number;
  /** Fim da janela de 24h da Meta; depois disso só template. */
  janelaExpiraEm: string | null;
  acompanharEm: string | null;
  acompanharNota: string | null;
  encerradaEm: string | null;
  /** A pessoa logada acompanha a conversa por encaminhamento interno. */
  souSeguidora: boolean;
  /** Último pedido criado pelo chat nesta conversa. */
  pedido: { numero: number | null; status: StatusPedidoChat } | null;
  /** Há carrinho em montagem (ainda não virou pedido). */
  temCarrinho: boolean;
  /** Modo Ponte: o que a IA entendeu enquanto conduziu (ou null). */
  ponte: ResumoPonte | null;
  /** A equipe desligou o Modo Ponte nesta conversa. */
  ponteDesligada: boolean;
  /** Chegou com a loja fechada e ninguém da equipe respondeu ainda (fila de retomada). */
  retomarDesde: string | null;
}

export interface MensagemDto {
  id: string;
  conversaId: string;
  direcao: DirecaoMensagem;
  autor: AutorMensagem;
  autorUsuario: { id: string; nome: string } | null;
  tipo: TipoMensagem;
  texto: string | null;
  conteudo: Record<string, unknown>;
  midia: {
    id: string;
    mimeType: string | null;
    nomeArquivo: string | null;
    tamanhoBytes: number | null;
    /** false enquanto o arquivo ainda está sendo baixado da Meta. */
    disponivel: boolean;
    /** Arquivo apagado (retenção de dados ou anonimização LGPD). */
    removida?: boolean;
  } | null;
  respostaAId: string | null;
  /** Trecho da mensagem citada (resposta). */
  respostaA: { texto: string; direcao: DirecaoMensagem; tipo: TipoMensagem } | null;
  /** Reações da cliente a esta mensagem. */
  reacoes: string[];
  status: StatusMensagem;
  erro: string | null;
  criadoEm: string;
}

export const enviarMensagemSchema = z.object({
  /** Gerado no cliente: permite UI otimista e torna o reenvio idempotente. */
  idCliente: z.string().uuid(),
  texto: z.string().trim().min(1).max(4096),
  interna: z.boolean().default(false),
  respostaAId: z.string().uuid().nullish(),
  /** Veio de uma sugestão da IA (registro para o aprendizado). */
  sugestao: usoSugestaoSchema.nullish(),
});
export type EnviarMensagemEntrada = z.infer<typeof enviarMensagemSchema>;

export const filtroConversasSchema = z.object({
  escopo: z.enum(["minhas", "sem_responsavel", "todas"]).default("minhas"),
  status: z.enum(["abertas", "retomada", "acompanhamento", "encerradas"]).default("abertas"),
  busca: z.string().trim().max(100).optional(),
  empresaId: z.string().uuid().optional(),
  /** Caixa de entrada (canal); sem ela, todas. */
  canalId: z.string().uuid().optional(),
});
export type FiltroConversas = z.infer<typeof filtroConversasSchema>;

/** Eventos de tempo real (SSE). Pequenos de propósito: a interface usa o id
 * para invalidar o cache e buscar o dado completo pela API. */
export type EventoTempoReal =
  | { tipo: "sugestoes.novas"; conversaId: string; responsavelId: string | null }
  | { tipo: "visao.atualizada"; conversaId: string; responsavelId: string | null }
  | { tipo: "mensagem.nova"; conversaId: string; mensagemId: string; responsavelId: string | null }
  | {
      tipo: "mensagem.status";
      conversaId: string;
      mensagemId: string;
      responsavelId: string | null;
    }
  | {
      tipo: "conversa.atualizada";
      conversaId: string;
      responsavelId: string | null;
      /** Responsável anterior, quando a conversa mudou de mãos (para sair da lista dela). */
      anteriorId?: string | null;
    }
  | { tipo: "usuario.presenca"; usuarioId: string }
  /** Só para gerência e administração. */
  | { tipo: "alerta.novo"; alertaId: string; apenasGestao: true }
  /** Avisos pessoais: lembrete de acompanhamento ou encaminhamento interno. */
  | {
      tipo: "aviso.pessoal";
      paraUsuarioId: string;
      conversaId: string;
      titulo: string;
      texto: string | null;
    };

/** Tela Clientes: contatos com vínculo ERP e a última conversa. */
export interface ClienteLista {
  contatoId: string;
  nome: string;
  telefone: string;
  erpClienteId: string | null;
  prioridade: number;
  vendedoraPreferencial: string | null;
  conversas: number;
  ultimaConversa: {
    id: string;
    status: StatusConversa;
    responsavel: string | null;
    em: string | null;
    /** A pessoa logada pode abrir esta conversa. */
    podeAbrir: boolean;
  } | null;
}

export const filtroClientesSchema = z.object({
  busca: z.string().trim().max(100).optional(),
  tipo: z.enum(["todos", "erp", "novos", "prioridade"]).default("todos"),
  pagina: z.coerce.number().int().min(0).max(500).default(0),
});
